import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";
const en = {
  noMonthlyTrend:
    "The total is available, but no monthly breakdown is published for this selection.",
  iranScope: "Iran",
  worldScope: "World · includes Iran",
  worldContext: "Global · all countries including Iran",
  patternReturned: "renewed reporting after a quieter interval",
  title: "Community threat landscape",
  intro: "Explore reported threats by community, location and time.",
  overview: "Overview",
  geography: "Geography",
  trends: "Trends & recurrence",
  crossBorder: "Cross-border lens",
  search: "Search published fields",
  searchHint: "Community, threat, platform, location or month",
  all: "All reporting",
  outside: "Outside Iran",
  reviewed: "Reviewed cross-border cases",
  location: "Residence",
  community: "Community",
  platform: "Platform",
  threat: "Reported threat",
  reset: "Reset filters",
  any: "All",
  world: "World",
  iran: "Iran provinces",
  mapTitle: "Where reports come from",
  mapHelp:
    "Select a country or province. Location is residence, not attacker origin.",
  matrix: "Communities × reported threats",
  matrixHelp:
    "Select a published combination to explore it. — means withheld or unavailable, not zero.",
  timeline: "Reports received over time",
  dateHelp:
    "Receipt date, not attack date. Current incomplete months are excluded. Gaps are not zero.",
  recurring: "Patterns that return",
  recurrenceHelp:
    "An earlier reporting wave, two lower observed months, then renewed reporting. A repeated category does not establish the same campaign or actor.",
  noSignal:
    "No returning signal is supported by the published time series in this selection.",
  returning: "Returning reporting pattern",
  earlier: "Earlier wave",
  renewed: "Renewed reporting",
  insufficient:
    "This combination has not been released. Broaden your selection or reset filters; unavailable does not mean no threats.",
  cases: "Distinct helpdesk cases",
  caseHelp:
    "Cases are not unique people, verified attacks or population risk. Support activities remain separate in the import ledger.",
  method: "Methods & limitations",
  period: "Published reporting window",
  before: "Complete months before",
  legacy:
    "The published category counts remain available. Geographic and combined views need a separately reviewed publication.",
  noGeo: "No geographic groups are published for this selection.",
  platforms: "Platforms in this selection",
  requests: "Request categories",
  categories: "Published category explorer",
  details: "View data table",
  close: "Close",
  province: "Province",
  selected: "Current selection",
  noSearch: "No published labels match your search.",
  publicSearch:
    "Search matches approved aggregate labels only. Private ticket descriptions, identities and evidence are not searchable here.",
  unknown: "No public estimate",
  mapLegend:
    "Highlighted areas have published reporting; shading is not a population risk scale.",
  outsideHelp:
    "Outside Iran is a residence context. Unknown residence is excluded from this scope, never guessed from nationality.",
  reviewHelp:
    "The historical form does not establish state or proxy attribution. Reviewed cross-border cases require documented evidence, reviewer, date and reasoning. This classification has not been collected for this publication.",
  privacy:
    "Counts are ranges of ten. Small groups and small complements are withheld. Fixed reviewed views reduce disclosure; they are not a person-level anonymity guarantee. Arbitrary raw-record queries are never exposed.",
  responses: "Protective account actions",
  sources: "Research & methodology",
  dateRange: "Date range",
  dateFrom: "From",
  dateTo: "To",
  applyDates: "Apply dates",
  allDates: "All dates",
  invalidDates:
    "Choose a From date on or before To, within the available calendar range.",
  datesUnavailable:
    "No searchable date range is available in this publication.",
  datesNeedPublication:
    "Date search requires this dataset to be republished after review. No re-upload is needed.",
  datePublicationHelp:
    "Publishing this reviewed dashboard enables public date search. You do not need to upload the file again.",
  periodLoading: "Loading reporting for the selected dates…",
  periodError:
    "This date range could not be loaded. Retry or return to all dates.",
  retryPeriod: "Retry range",
  country: "Country",
  smallCountryHelp:
    "Some published countries have no boundary at this map scale. Their reporting remains available in the country list.",
  publishGeo: "Review geographic and combined publication",
  confirmGeo:
    "I reviewed the country/province groups, community × threat combinations monthly views and historical date filtering below for public release.",
  geoReviewHelp:
    "Required for full dashboard publication: review the map, community–threat combinations and monthly trends before confirming. Unknown geography is never assigned to a place; no cross-border attribution is inferred.",
  view: "View",
  chooseView: "Review an aggregate view",
  updated: "Published",
  global: "Global reporting",
  disclosure: "Publication threshold",
  records: "Support work",
  noAttacks: "No reported attack groups meet the publication threshold.",
  scope: "Scope",
  provinceExplorer: "Province explorer",
  reportedPatterns: "Published reporting patterns",
  publishedTrends: "Published trend",
  chartUnavailable:
    "The chart could not be loaded. Published summaries and data tables on this page remain available.",
  reportVolume: "Report volume",
  lower: "Lower",
  higher: "Higher",
  withheld: "Small groups withheld",
  explorePattern: "Explore this pattern",
  morePatterns: "More returning patterns",
  recurrenceCaution:
    "A repeated threat type does not establish the same campaign.",
  crossBorderSummary:
    "Outside Iran describes residence. Reviewed cross-border cases require evidence assessed by a human reviewer.",
  reviewCriteria: "Review criteria",
  selectedArea: "Selected area",
  publishedAreas: "Published reporting",
  higherReporting: "More published reports",
  lowerReporting: "Fewer published reports",
  unpublishedAreas: "Unavailable or withheld",
  awaitingPublication: "Geographic reporting is awaiting publication",
  categoryPublication: "Category summaries available",
  awaitingPublicationHelp:
    "The current release includes the category summaries below. Geographic reporting and community–threat combinations are not available in this publication.",
} as const;
const fa: Record<keyof typeof en, string> = {
  noMonthlyTrend:
    "آمار کل موجود است، اما برای این انتخاب تفکیک ماهانه‌ای منتشر نشده است.",
  iranScope: "ایران",
  worldScope: "جهان · شامل ایران",
  worldContext: "جهانی · همهٔ کشورها شامل ایران",
  patternReturned: "بازگشت گزارش‌ها پس از یک دورهٔ کم‌گزارش",
  title: "چشم‌انداز تهدیدهای کامیونیتی",
  intro: "الگوهای گزارش‌شده را بر اساس کامیونیتی، محل سکونت و زمان بررسی کنید.",
  overview: "نمای کلی",
  geography: "جغرافیا",
  trends: "روندها و الگوهای بازگشتی",
  crossBorder: "فشار فرامرزی",
  search: "جست‌وجو در فیلدهای منتشرشده",
  searchHint: "کامیونیتی، تهدید، پلتفرم، مکان یا ماه",
  all: "همهٔ گزارش‌ها",
  outside: "خارج از ایران",
  reviewed: "موارد فرامرزی بررسی‌شده",
  location: "محل سکونت",
  community: "کامیونیتی",
  platform: "پلتفرم",
  threat: "تهدید گزارش‌شده",
  reset: "پاک‌کردن فیلترها",
  any: "همه",
  world: "جهان",
  iran: "استان‌های ایران",
  mapTitle: "گزارش‌ها از کجا می‌آیند",
  mapHelp: "کشور یا استان را انتخاب کنید. مکان، محل سکونت است نه منشأ حمله.",
  matrix: "کامیونیتی‌ها و تهدیدهای گزارش‌شده",
  matrixHelp:
    "یک ترکیب منتشرشده را انتخاب کنید. خط تیره یعنی منتشرنشده یا ناموجود، نه صفر.",
  timeline: "روند دریافت گزارش‌ها",
  dateHelp:
    "تاریخ دریافت گزارش، نه وقوع حمله. ماه جاریِ ناقص محاسبه نمی‌شود. فاصله‌ها صفر نیستند.",
  recurring: "الگوهایی که برمی‌گردند",
  recurrenceHelp:
    "موج قبلی گزارش، دو ماه مشاهده‌شده با گزارش کمتر، سپس افزایش دوباره. تکرار یک دسته به معنی همان کمپین یا مهاجم نیست.",
  noSignal:
    "سری زمانی منتشرشده در این انتخاب از الگوی بازگشتی پشتیبانی نمی‌کند.",
  returning: "الگوی بازگشتی در گزارش‌ها",
  earlier: "موج قبلی",
  renewed: "افزایش دوباره",
  insufficient:
    "این ترکیب منتشر نشده است. انتخاب را گسترده‌تر کنید یا فیلترها را پاک کنید؛ نبود داده به معنی نبود تهدید نیست.",
  cases: "کیس‌های متمایز هلپ‌دسک",
  caseHelp:
    "کیس‌ها تعداد افراد یکتا، حملات اثبات‌شده یا ریسک جمعیت نیستند. فعالیت‌های پشتیبانی جداگانه در دفتر واردات حفظ می‌شوند.",
  method: "روش و محدودیت‌ها",
  period: "بازهٔ گزارش منتشرشده",
  before: "ماه‌های کامل پیش از",
  legacy:
    "آمار دسته‌بندی‌های منتشرشده در دسترس است. نمای جغرافیایی و ترکیبی نیازمند انتشار جداگانه پس از بازبینی است.",
  noGeo: "در این انتخاب هیچ گروه جغرافیایی منتشر نشده است.",
  platforms: "پلتفرم‌ها در این انتخاب",
  requests: "موضوع درخواست‌ها",
  categories: "کاوش دسته‌های منتشرشده",
  details: "جدول داده‌ها",
  close: "بستن",
  province: "استان",
  selected: "انتخاب فعلی",
  noSearch: "هیچ برچسب منتشرشده‌ای با جست‌وجو تطابق ندارد.",
  publicSearch:
    "جست‌وجو فقط روی برچسب‌های تجمیعیِ تأییدشده است. شرح خصوصی تیکت، هویت و شواهد اینجا قابل جست‌وجو نیستند.",
  unknown: "برآورد عمومی موجود نیست",
  mapLegend:
    "مناطق مشخص‌شده دارای گزارش منتشرشده‌اند؛ رنگ‌ها مقیاس ریسک جمعیت نیستند.",
  outsideHelp:
    "خارج از ایران بر اساس محل سکونت است. اقامت نامشخص از این نما کنار گذاشته می‌شود و از ملیت حدس زده نمی‌شود.",
  reviewHelp:
    "فرم تاریخی انتساب به دولت یا عوامل وابسته را مشخص نمی‌کند. موارد فرامرزی بررسی‌شده به شواهد مستند، مسئول بررسی، تاریخ و دلیل نیاز دارند. این طبقه‌بندی برای این مجموعه گردآوری نشده است.",
  privacy:
    "شمارش‌ها بازهٔ ده‌تایی‌اند. گروه‌های کوچک و مکمل‌های کوچک پنهان می‌شوند. نماهای ثابتِ بازبینی‌شده افشا را کاهش می‌دهند؛ تضمین ناشناس‌بودن افراد نیستند. پرس‌وجوی دلخواه روی رکورد خام ارائه نمی‌شود.",
  responses: "اقدام‌های حمایتی حساب",
  sources: "پژوهش و روش‌شناسی",
  dateRange: "بازهٔ تاریخ",
  dateFrom: "از",
  dateTo: "تا",
  applyDates: "اعمال بازه",
  allDates: "همهٔ تاریخ‌ها",
  invalidDates:
    "تاریخ از باید پیش از تاریخ تا یا برابر آن و داخل محدودهٔ تقویم موجود باشد.",
  datesUnavailable: "بازهٔ تاریخیِ قابل جست‌وجویی برای این انتشار موجود نیست.",
  datesNeedPublication:
    "برای فعال‌شدن جست‌وجوی تاریخ، همین داده‌ها باید پس از بازبینی دوباره منتشر شوند. آپلود دوباره لازم نیست.",
  datePublicationHelp:
    "انتشار این داشبوردِ بازبینی‌شده، جست‌وجوی تاریخ را در صفحهٔ عمومی فعال می‌کند. آپلود دوبارهٔ فایل لازم نیست.",
  periodLoading: "در حال دریافت گزارش بازهٔ انتخاب‌شده…",
  periodError:
    "این بازه دریافت نشد. دوباره تلاش کنید یا همهٔ تاریخ‌ها را ببینید.",
  retryPeriod: "تلاش دوباره",
  country: "کشور",
  smallCountryHelp:
    "برخی کشورهای منتشرشده در این مقیاس مرز قابل نمایش ندارند. گزارش آن‌ها همچنان از فهرست کشورها قابل مشاهده است.",
  publishGeo: "بازبینی انتشار جغرافیایی و ترکیبی",
  confirmGeo:
    "گروه‌های کشور و استان، ترکیب کامیونیتی و تهدید ، نماهای ماهانه و فیلتر تاریخی زیر را برای انتشار عمومی بازبینی کردم.",
  geoReviewHelp:
    "برای انتشار داشبورد کامل، ابتدا نقشه، ترکیب گروه‌–تهدید و ترندهای ماهانه را بازبینی و تأیید کنید. مکان نامشخص به جایی تخصیص نمی‌یابد و انتساب فرامرزی حدس زده نمی‌شود.",
  view: "نما",
  chooseView: "بازبینی یک نمای تجمیعی",
  updated: "منتشرشده",
  global: "گزارش جهانی",
  disclosure: "حداقل انتشار",
  records: "فعالیت پشتیبانی",
  noAttacks: "هیچ گروه حملهٔ گزارش‌شده‌ای حداقل انتشار را ندارد.",
  scope: "دامنه",
  provinceExplorer: "کاوش استان‌ها",
  reportedPatterns: "الگوهای گزارشِ منتشرشده",
  publishedTrends: "روند منتشرشده",
  chartUnavailable:
    "نمودار بارگیری نشد. خلاصه‌ها و جدول‌های دادهٔ منتشرشده در این صفحه همچنان در دسترس‌اند.",
  reportVolume: "حجم گزارش",
  lower: "کمتر",
  higher: "بیشتر",
  withheld: "گروه‌های کوچک پنهان‌اند",
  explorePattern: "بررسی این الگو",
  morePatterns: "الگوهای بازگشتی بیشتر",
  recurrenceCaution: "تکرار یک نوع تهدید به معنی اثبات همان کمپین نیست.",
  crossBorderSummary:
    "خارج از ایران یعنی محل سکونت. موارد فرامرزی بررسی‌شده به شواهدی نیاز دارند که مسئول بررسی ارزیابی کرده باشد.",
  reviewCriteria: "معیارهای بررسی",
  selectedArea: "منطقهٔ انتخاب‌شده",
  publishedAreas: "گزارش منتشرشده",
  higherReporting: "گزارش منتشرشدهٔ بیشتر",
  lowerReporting: "گزارش منتشرشدهٔ کمتر",
  unpublishedAreas: "منتشرنشده یا پنهان",
  awaitingPublication: "آمار جغرافیایی در انتظار انتشار است",
  categoryPublication: "آمار دسته‌بندی‌ها موجود است",
  awaitingPublicationHelp:
    "در انتشار فعلی، آمار دسته‌بندی‌های پایین موجود است. گزارش جغرافیایی و ترکیب کامیونیتی و تهدید هنوز در این مجموعه منتشر نشده‌اند.",
};
export type ObservatoryCopy = keyof typeof en;
export function useObservatoryCopy() {
  const { locale } = useWorkspaceCopy();
  return {
    locale,
    t: (key: ObservatoryCopy) => (locale === "fa" ? fa : en)[key],
  };
}
